import { SlideHeader } from "../../../SlideHeader";

const SECURITY_PAGE_URL = "https://www.apptonomy.ai/security";

interface ControlGroup {
  heading: string;
  items: string[];
}

/**
 * Appendix G — the trust questions an agency or enterprise buyer asks before
 * connecting a store console, and the honest answer to the one we cannot yet
 * give (SOC 2).
 *
 * Every claim on this slide is already published on /security and carried in
 * the security claim-evidence register (a7y-webapp/src/marketing/
 * security-content.ts, last verified 2026-08-07). Nothing here is new copy —
 * that is deliberate, so the deck and the public page cannot drift apart.
 *
 * Do NOT add SOC 2, certification, or "enterprise-grade" language. It is
 * prohibited by security-policy.ts and guarded by security-content.test.ts.
 */
const CONTROLS: ControlGroup[] = [
  {
    heading: "Customer data",
    items: [
      "Private connected-account metrics stay scoped to the account — never pooled into cross-customer benchmarks, resold, or used to build market estimates.",
      "Store credentials encrypted in the browser before upload, then application-encrypted for persistence with Google Cloud KMS.",
      "Keyless Google Play linking — no customer service-account key is uploaded or stored.",
      "Per-purpose lifecycle rules on customer-data buckets; 90-day retention on application logs.",
    ],
  },
  {
    heading: "Access & publishing",
    items: [
      "Every store publish is initiated by an authorized project editor or admin, who reviews the content and chooses the scopes, fields, and locales.",
      "Access is revocable from the store console at any time.",
      "Deletion on request cascades through projects, credentials, org membership, analytics profiles, and identity records.",
    ],
  },
  {
    heading: "Engineering controls",
    items: [
      "Enforced CSP and security headers on every route.",
      "Secret scanning enforced in CI; a full git-history audit was completed and every surfaced credential rotated.",
      "A rotation is refused while a deployed artifact still logs the value it would rotate.",
      "Metadata-only retention on AI-gateway traffic; historical secret-bearing logs redacted.",
    ],
  },
];

export default function TrustAndDataHandling() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:pl-24 md:pr-20 md:pt-16 md:pb-10 md:[&_header]:mb-6">
      <SlideHeader unnumbered eyebrow="Appendix G · Trust & Data Handling">
        <h1 className="text-2xl font-semibold leading-tight tracking-tight md:text-4xl md:leading-[1.15]">
          Everything an agency or enterprise buyer asks before connecting a
          store console — <span className="text-blue-700">including what we
          don&rsquo;t claim</span>.
        </h1>
      </SlideHeader>

      <div className="grid grid-cols-1 gap-3 md:flex-1 md:grid-cols-3 md:gap-4">
        {CONTROLS.map((group) => (
          <div
            key={group.heading}
            className="rounded-xl bg-white p-4 ring-1 ring-slate-200 md:p-5"
          >
            <p className="text-sm font-bold uppercase tracking-[0.12em] text-blue-700 md:text-base">
              {group.heading}
            </p>
            <ul className="mt-3 flex flex-col gap-2">
              {group.items.map((item) => (
                <li
                  key={item}
                  className="flex gap-2 text-xs leading-snug text-slate-700 md:text-sm"
                >
                  <span
                    aria-hidden
                    className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* The honest band. This is the point of the appendix — a buyer who hears
          only the three columns above will assume assurance we have not earned. */}
      <div className="mt-4 rounded-2xl bg-amber-50 px-5 py-4 ring-1 ring-amber-200 md:mt-5 md:px-8 md:py-5">
        <p className="text-sm font-bold uppercase tracking-[0.12em] text-amber-800 md:text-base">
          What we don&rsquo;t claim
        </p>
        <p className="mt-2 text-xs leading-snug text-amber-950 md:text-sm">
          Apptonomy has{" "}
          <span className="font-semibold">no SOC 2 report, and no SOC 2
          examination is underway</span>. The controls above are current
          practices; they have not been independently examined. If a SOC 2
          report is mandatory for a buyer&rsquo;s procurement, we say so before
          they connect private store data. A published vulnerability-disclosure
          policy, a DPA, and a subprocessor list are available today.
        </p>
        <p className="mt-2 text-xs leading-snug text-amber-900 md:text-sm">
          All of the above is public, not deck-only —{" "}
          <a
            href={SECURITY_PAGE_URL}
            target="_blank"
            rel="noreferrer"
            className="font-semibold underline decoration-amber-400 underline-offset-2 hover:decoration-amber-700"
          >
            apptonomy.ai/security
          </a>
          , reviewed 2026-08-07.
        </p>
      </div>
    </div>
  );
}
